import { useEffect, useMemo, useState } from 'react'
import {
  Library,
  Download,
  Pencil,
  Search,
  Trash2,
  Upload,
  LayoutGrid,
  List as ListIcon,
  ChevronLeft,
  ChevronRight,
  Columns3,
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/hooks/use-toast'
import { usePagination } from '@/hooks/use-pagination'
import {
  useRepositoryAssets,
  useDemoteRepositoryAsset,
  useDownloadContent,
  useProducts,
} from '@/hooks/use-api'
import { usePicklistOptions } from '@/hooks/use-picklists'
import type { RepositoryAsset, RepositorySection } from '@/types'
import { MarketingPanel } from '@/pages/repository/MarketingPanel'
import { UploadContentModal } from '@/components/UploadContentModal'
import { RepositoryAssetEditModal } from '@/components/RepositoryAssetEditModal'
import { RepositoryAssetThumbnail } from '@/components/RepositoryAssetThumbnail'
import { RepositoryAssetRow } from '@/components/RepositoryAssetRow'
import { cn, toDisplayDate } from '@/lib/utils'
import { explainError } from '@/lib/explain-error'

type ViewMode = 'card' | 'list'
const PAGE_SIZE_OPTIONS = [12, 24, 48, 96] as const
const DEFAULT_PAGE_SIZE = 24
const VIEW_KEY = 'repository.view'
const PAGE_SIZE_KEY = 'repository.pageSize'

// Thumbnails per row (card grids). A slider lets the user tune density freely;
// applied via inline gridTemplateColumns so any column count works without
// relying on Tailwind generating grid-cols-N literals. minmax(0,1fr) keeps the
// columns from overflowing the container on narrow screens.
const COLUMNS_KEY = 'repository.columns'
const MIN_COLUMNS = 1
const MAX_COLUMNS = 6
const DEFAULT_COLUMNS = 3

function clampColumns(n: number): number {
  if (!Number.isFinite(n)) return DEFAULT_COLUMNS
  return Math.min(MAX_COLUMNS, Math.max(MIN_COLUMNS, Math.round(n)))
}

function readStoredColumns(): number {
  if (typeof window === 'undefined') return DEFAULT_COLUMNS
  const raw = window.localStorage.getItem(COLUMNS_KEY)
  return raw ? clampColumns(parseInt(raw, 10)) : DEFAULT_COLUMNS
}

function gridColumnsStyle(columns: number) {
  return { gridTemplateColumns: `repeat(${clampColumns(columns)}, minmax(0, 1fr))` }
}

function formatFileSize(bytes?: number) {
  if (!bytes) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB']
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1)
  return `${Math.round((bytes / Math.pow(1024, i)) * 10) / 10} ${units[i]}`
}

function formatDate(iso?: string) {
  if (!iso) return ''
  return (toDisplayDate(iso) ?? new Date(iso)).toLocaleDateString()
}

function readStoredView(): ViewMode {
  if (typeof window === 'undefined') return 'card'
  const v = window.localStorage.getItem(VIEW_KEY)
  return v === 'list' ? 'list' : 'card'
}

function readStoredPageSize(): number {
  if (typeof window === 'undefined') return DEFAULT_PAGE_SIZE
  const raw = window.localStorage.getItem(PAGE_SIZE_KEY)
  const n = raw ? parseInt(raw, 10) : NaN
  return PAGE_SIZE_OPTIONS.includes(n as any) ? n : DEFAULT_PAGE_SIZE
}

interface SectionPanelProps {
  section: RepositorySection
  view: ViewMode
  pageSize: number
  columns: number
}

function SectionPanel({ section, view, pageSize, columns }: SectionPanelProps) {
  const { toast } = useToast()
  const [search, setSearch] = useState('')
  const [assetType, setAssetType] = useState<string>('all')
  const [productId, setProductId] = useState<string>('all')
  const [editingAsset, setEditingAsset] = useState<RepositoryAsset | null>(null)
  const [uploadOpen, setUploadOpen] = useState(false)

  const ASSET_TYPES = usePicklistOptions('content', 'asset_type')

  const { page, nextPage, prevPage, firstPage, extract } = usePagination()

  // Reset to page 1 whenever filters or page size change.
  useEffect(() => {
    firstPage()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section, assetType, productId, search, pageSize])

  const params = useMemo(
    () => ({
      section,
      asset_type: assetType === 'all' ? undefined : assetType,
      product_id: section === 'Product' && productId !== 'all' ? productId : undefined,
      search: search.trim() || undefined,
      page,
      limit: pageSize,
    }),
    [section, assetType, productId, search, page, pageSize]
  )

  const { data, isLoading, error } = useRepositoryAssets(params)
  const { data: productsResponse } = useProducts(
    section === 'Product' ? { limit: 1000 } : undefined
  )
  const products = productsResponse?.data || []

  const demote = useDemoteRepositoryAsset()
  const download = useDownloadContent()

  const { items, total, currentPage, totalPages, hasNextPage, hasPrevPage } = extract(
    data as { data?: RepositoryAsset[]; total?: number; page?: number; limit?: number } | undefined
  )
  const assets = items as RepositoryAsset[]

  const handleDownload = async (asset: RepositoryAsset) => {
    try {
      const blob = await download.mutateAsync(asset.id)
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = asset.path_on_client || asset.title
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (err) {
      toast({
        title: 'Download failed',
        description: explainError(err, 'Could not download file'),
        variant: 'destructive',
      })
    }
  }

  const handleDemote = async (asset: RepositoryAsset) => {
    if (
      !confirm(`Remove "${asset.title}" from the repository? The file itself will not be deleted.`)
    )
      return
    try {
      await demote.mutateAsync(asset.id)
      toast({ title: 'Removed from repository', description: asset.title })
    } catch (err) {
      toast({
        title: 'Could not remove',
        description: explainError(err, 'Unknown error'),
        variant: 'destructive',
      })
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-1 flex-col gap-3 md:flex-row md:items-center">
          <div className="relative flex-1 max-w-md">
            <Search
              className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
            <Input
              type="search"
              placeholder={`Search ${section.toLowerCase()} documents…`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              aria-label={`Search ${section} documents`}
            />
          </div>
          <Select value={assetType} onValueChange={setAssetType}>
            <SelectTrigger className="w-44" aria-label="Filter by asset type">
              <SelectValue placeholder="All types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {ASSET_TYPES.map((opt) => (
                <SelectItem key={opt} value={opt}>
                  {opt}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {section === 'Product' && (
            <Select value={productId} onValueChange={setProductId}>
              <SelectTrigger className="w-56" aria-label="Filter by product">
                <SelectValue placeholder="All products" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All products</SelectItem>
                {products.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
        <Button onClick={() => setUploadOpen(true)} aria-label="Upload new document to repository">
          <Upload className="mr-2 h-4 w-4" aria-hidden="true" />
          Upload
        </Button>
      </div>

      {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {error && (
        <p className="text-sm text-destructive">
          Could not load repository: {explainError(error, 'unknown error')}
        </p>
      )}
      {!isLoading && !error && assets.length === 0 && (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            No {section.toLowerCase()} documents yet. Click <strong>Upload</strong> to add one.
          </CardContent>
        </Card>
      )}

      {assets.length > 0 && view === 'card' && (
        <div className="grid gap-3" style={gridColumnsStyle(columns)}>
          {assets.map((asset) => (
            <Card key={asset.id} className="flex flex-col overflow-hidden">
              <RepositoryAssetThumbnail
                contentVersionId={asset.id}
                fileType={asset.file_type}
                hasThumbnail={!!asset.thumbnail_url}
                title={asset.title}
                className="rounded-none border-0 border-b"
              />
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2">
                  <CardTitle className="text-base leading-tight">{asset.title}</CardTitle>
                  <Badge variant="secondary" className="shrink-0">
                    {asset.asset_type || '—'}
                  </Badge>
                </div>
                {asset.repository_summary && (
                  <CardDescription className="line-clamp-3">
                    {asset.repository_summary}
                  </CardDescription>
                )}
              </CardHeader>
              <CardContent className="flex flex-1 flex-col justify-between gap-3">
                <div className="space-y-2 text-xs text-muted-foreground">
                  <div className="flex flex-wrap gap-2">
                    {asset.file_type && <Badge variant="outline">{asset.file_type}</Badge>}
                    <span>{formatFileSize(asset.content_size)}</span>
                    <span>•</span>
                    <span>Updated {formatDate(asset.last_modified_date)}</span>
                  </div>
                  {asset.owner_name && <div>Owner: {asset.owner_name}</div>}
                  {section === 'Product' && asset.products && asset.products.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {asset.products.map((p) => (
                        <Badge key={`${asset.id}-${p.product_id}`} variant="outline">
                          {p.product_name || p.product_id}
                        </Badge>
                      ))}
                    </div>
                  )}
                  {asset.tags && asset.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {asset.tags.map((tag) => (
                        <Badge
                          key={`${asset.id}-tag-${tag}`}
                          variant="outline"
                          className="text-[10px]"
                        >
                          #{tag}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-end gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDownload(asset)}
                    aria-label={`Download ${asset.title}`}
                  >
                    <Download className="h-4 w-4" aria-hidden="true" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditingAsset(asset)}
                    aria-label={`Edit ${asset.title}`}
                  >
                    <Pencil className="h-4 w-4" aria-hidden="true" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDemote(asset)}
                    aria-label={`Remove ${asset.title} from repository`}
                  >
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {assets.length > 0 && view === 'list' && (
        <div className="space-y-2">
          {assets.map((asset) => (
            <RepositoryAssetRow
              key={asset.id}
              asset={asset}
              section={section}
              onDownload={handleDownload}
              onEdit={setEditingAsset}
              onDemote={handleDemote}
            />
          ))}
        </div>
      )}

      {assets.length > 0 && (
        <nav
          className="flex flex-col items-center justify-between gap-2 pt-2 sm:flex-row"
          aria-label="Pagination"
        >
          <div className="text-xs text-muted-foreground">
            {total > 0 && (
              <>
                Page {currentPage} of {totalPages || 1} · {total} result{total === 1 ? '' : 's'}
              </>
            )}
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => prevPage()}
              disabled={!hasPrevPage}
              aria-label="Previous page"
            >
              <ChevronLeft className="h-4 w-4" aria-hidden="true" />
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => nextPage()}
              disabled={!hasNextPage}
              aria-label="Next page"
            >
              Next
              <ChevronRight className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
        </nav>
      )}

      {editingAsset && (
        <RepositoryAssetEditModal
          asset={editingAsset}
          open={!!editingAsset}
          onClose={() => setEditingAsset(null)}
        />
      )}

      <UploadContentModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        repositoryDefaults={{ section, asset_type: ASSET_TYPES[0] || 'Other' }}
      />
    </div>
  )
}

export default function Repository() {
  const [tab, setTab] = useState<RepositorySection>('Product')
  const [view, setView] = useState<ViewMode>(() => readStoredView())
  const [pageSize, setPageSize] = useState<number>(() => readStoredPageSize())
  const [columns, setColumns] = useState<number>(() => readStoredColumns())

  useEffect(() => {
    try {
      window.localStorage.setItem(VIEW_KEY, view)
    } catch (_) {
      /* ignore */
    }
  }, [view])

  useEffect(() => {
    try {
      window.localStorage.setItem(COLUMNS_KEY, String(columns))
    } catch (_) {
      /* ignore */
    }
  }, [columns])

  useEffect(() => {
    try {
      window.localStorage.setItem(PAGE_SIZE_KEY, String(pageSize))
    } catch (_) {
      /* ignore */
    }
  }, [pageSize])

  return (
    <div className="container mx-auto px-4 py-8">
      <header className="mb-6 flex items-center gap-3">
        <Library className="h-6 w-6 text-primary" aria-hidden="true" />
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Repository</h1>
          <p className="text-sm text-muted-foreground">
            Browse curated product collateral and company documents.
          </p>
        </div>
      </header>

      <Tabs value={tab} onValueChange={(v) => setTab(v as RepositorySection)}>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <TabsList>
            <TabsTrigger value="Product">Products</TabsTrigger>
            <TabsTrigger value="Company">Company</TabsTrigger>
            <TabsTrigger value="Marketing">Marketing</TabsTrigger>
          </TabsList>

          {/* The thumbnails-per-row slider applies to every card grid (incl.
              Marketing); view mode + page size apply to the Product/Company
              panels only (Marketing manages its own sort and pagination). */}
          <div className="flex items-center gap-2">
            {tab !== 'Marketing' && (
              <div
                className="inline-flex items-center rounded-md border bg-background p-0.5"
                role="group"
                aria-label="View mode"
              >
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setView('card')}
                  aria-pressed={view === 'card'}
                  aria-label="Card view"
                  className={cn('h-7 px-2', view === 'card' && 'bg-accent text-accent-foreground')}
                >
                  <LayoutGrid className="h-4 w-4" aria-hidden="true" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setView('list')}
                  aria-pressed={view === 'list'}
                  aria-label="List view"
                  className={cn('h-7 px-2', view === 'list' && 'bg-accent text-accent-foreground')}
                >
                  <ListIcon className="h-4 w-4" aria-hidden="true" />
                </Button>
              </div>
            )}

            {/* Thumbnails per row — slide for finer control. Shown wherever a card
                grid is visible: Marketing always, Product/Company in card view. */}
            {(tab === 'Marketing' || view === 'card') && (
              <div className="inline-flex h-9 items-center gap-2 rounded-md border bg-background px-2.5">
                <Columns3 className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                <input
                  type="range"
                  min={MIN_COLUMNS}
                  max={MAX_COLUMNS}
                  step={1}
                  value={columns}
                  onChange={(e) => setColumns(clampColumns(parseInt(e.target.value, 10)))}
                  aria-label="Thumbnails per row"
                  aria-valuetext={`${columns} per row`}
                  style={{ accentColor: 'hsl(var(--primary))' }}
                  className="h-1.5 w-24 cursor-pointer rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                />
                <span
                  className="w-12 text-xs tabular-nums text-muted-foreground"
                  aria-hidden="true"
                >
                  {columns}/row
                </span>
              </div>
            )}

            {tab !== 'Marketing' && (
              <Select value={String(pageSize)} onValueChange={(v) => setPageSize(parseInt(v, 10))}>
                <SelectTrigger className="w-32" aria-label="Items per page">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZE_OPTIONS.map((n) => (
                    <SelectItem key={n} value={String(n)}>
                      {n} per page
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        <TabsContent value="Product" className="mt-4">
          <SectionPanel section="Product" view={view} pageSize={pageSize} columns={columns} />
        </TabsContent>
        <TabsContent value="Company" className="mt-4">
          <SectionPanel section="Company" view={view} pageSize={pageSize} columns={columns} />
        </TabsContent>
        <TabsContent value="Marketing" className="mt-4">
          <MarketingPanel columns={columns} />
        </TabsContent>
      </Tabs>
    </div>
  )
}
